'use client';

import { useState } from 'react';
import { getPlatformFee } from '@peach/types/payments/fees.js';
import {
  PRICE_MAX,
  PRICE_MIN,
  TIP_TIERS,
  priceProblem,
} from '@peach/types/payments/price-limits.js';
import { formatPrice } from '@/lib/utils/format';

/** One of the tiers, or `other` with the amount typed beside it. */
type TipChoice = number | 'other';

const usd = (amount: number) => formatPrice({ amount, currency: 'USD' });

/** What a tip costs the client, said one way wherever a tip is picked or paid. */
export const tipFeeLine = (name: string, amount: number) =>
  `${name} gets the whole ${usd(amount)}. You pay ${usd(amount + getPlatformFee(amount))} with the Platform fee.`;
export const TIP_RANGE_HINT = `Whole dollars, ${usd(PRICE_MIN)} to ${usd(PRICE_MAX)}`;
export const TIP_PLACEHOLDER = 'Amount';

/**
 * The amount a tip picker stands on, for the header and the sheet alike: a tier, or a typed
 * whole-dollar amount in the server's range. `amount` is null while the typed one is not.
 */
export function useTipAmount(initial: number = TIP_TIERS[0]!) {
  // An amount off the tiers (one typed in the header) comes back as Other, already typed.
  const tier = TIP_TIERS.includes(initial);
  const [choice, setChoice] = useState<TipChoice>(tier ? initial : 'other');
  const [typed, setTyped] = useState(tier ? '' : String(initial));
  const amount =
    choice !== 'other' ? choice : typed && !priceProblem(Number(typed)) ? Number(typed) : null;
  return {
    choice,
    pick: setChoice,
    typed,
    // Whole dollars: "$1,000" reads as 1000 (refused, over the cap), "20.50" as 20.
    type: (value: string) => setTyped(value.replace(/,/g, '').split('.')[0]!.replace(/\D/g, '')),
    amount,
  };
}
